import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Step,
  StepLabel,
  Stepper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Select from "react-select";
import { toast } from "react-toastify";
import {
  useGetallergiesChronicDeaseseQuery,
  useGetmedicationSupplementQuery,
  useCreatePatientMutation,
  useUpdatePatientMutation,
  useGetPatientListQuery,
  useGetallHerediateryDeaseseQuery,
  useGetallDiagnosisDeaseseQuery
} from "../utils/apiSlice";
import { nameValidation, phoneValidation } from "../utils/validation";
import {
  formatHeightFtInInput,
  sanitizeHeightFtInTyping,
  validateHeightCm,
  validateHeightFtIn,
  validateWeightKg,
  validateWeightLbs,
} from "../utils/heightWeightFormat";
import * as Yup from "yup";
import { triggerCoins } from "../pages/canvasConfict";
import { CheckCircle } from "@mui/icons-material";
interface HealthQuestionnaireModalProps {
  open: boolean;
  onClose: () => void;
  userId: any;
  patientId?: any;
  initialData?: any;
  mode?: "create" | "update";
}

const HealthQuestionnaireModal: React.FC<HealthQuestionnaireModalProps> = ({
  open,
  onClose,
  userId,
  patientId: propPatientId,
  initialData,
  mode = "create",
}) => {
  const handleModalKeyDown = (e: React.KeyboardEvent) => {
    const isSpace =
      e.key === " " || e.code === "Space" || (e as any).key === "Spacebar";
    if (!isSpace) return;

    const anyReactSelectMenuOpen =
      !!document.querySelector(".react-select__menu .react-select__option");

    if (anyReactSelectMenuOpen) {
      e.preventDefault();
      e.stopPropagation();
    }
  };
  useEffect(() => {
    if (open) {
      setActiveStep(0);
    }
  }, [open]);

  const getStoredUser = () => {
    // Try localStorage
    const localData = localStorage.getItem("userData");
    if (localData) return JSON.parse(localData);

    // Fallback to sessionStorage
    const sessionData = sessionStorage.getItem("userData");
    if (sessionData) return JSON.parse(sessionData);

    // Return default if neither exists
    return {};
  }


  const storedUser = getStoredUser();
  const [activeStep, setActiveStep] = useState(0);
  const [units, setUnits] = useState({ height: "cm", weight: "kg" });
  const [errors, setErrors] = useState<any>({});

  const [conditionSearch, setConditionSearch] = useState("");
  const [allergySearch, setAllergySearch] = useState("");
  const [medicationSearch, setMedicationSearch] = useState("");
  const [supplementSearch, setSupplementSearch] = useState("");
  const [familySearch, setFamilySearch] = useState("");

  const [selectedConditions, setSelectedConditions] = useState<any[]>([]);
  const [selectedAllergies, setSelectedAllergies] = useState<any[]>([]);
  const [selectedMedications, setSelectedMedications] = useState<any[]>([]);
  const [selectedSupplements, setSelectedSupplements] = useState<any[]>([]);
  const [selectedFamilyHistory, setSelectedFamilyHistory] = useState<any[]>([]);

  const [createPatient, { isLoading: isCreateLoading }] = useCreatePatientMutation();
  const [updatePatient, { isLoading: isUpdateLoading }] = useUpdatePatientMutation();
  const isLoading = isCreateLoading || isUpdateLoading;

  const [questionnaireData, setQuestionnaireData] = useState<any>({
    firstName: "",
    middleName: "",
    lastName: "",
    bloodGroup: "",
    dateOfBirth: "",
    gender: null,
    sexAssignedAtBirth: null,
    height: "",
    weight: "",
    currentLocation: "",
    emergencyContactName: "",
    emergencyRelationship: "",
    emergencyPhoneNumber: "",
    emergencyContactConsent: null,
    diagnosedConditionIds: [],
    allergyIds: [],
    currentlyTakingMedications: null,
    patientMedicationIds: [],
    supplementIds: [],
    familyHistoryDiseaseIds: [],
    goals: "",
    finalInput: "",
  });

  const formatDecimal = (value: number) =>
    Number.isInteger(value) ? String(value) : value.toFixed(1).replace(/\.0$/, "");

  const inferHeightUnit = (value: any) => String(value || "").includes("'") ? "ft/in" : "cm";
  const inferWeightUnit = (value: any) => String(value || "").toLowerCase().includes("lb") ? "lbs" : "kg";

  const cmToFeetInches = (value: any) => {
    const cm = Number(value);
    if (!value || Number.isNaN(cm)) return "";
    const totalInches = Math.round(cm / 2.54);
    const feet = Math.floor(totalInches / 12);
    const inches = totalInches % 12;
    return `${feet}'${inches}"`;
  };

  const feetInchesToCm = (value: any) => {
    const match = String(value).match(/(\d+)\s*'\s*(\d{1,2})"?/);
    if (!match) return "";
    const feet = Number(match[1]);
    const inches = Number(match[2]);
    return String(Math.round((feet * 12 + inches) * 2.54));
  };

  const kgToLbs = (value: any) => {
    const kg = Number(value);
    if (!value || Number.isNaN(kg)) return "";
    return formatDecimal(kg * 2.20462);
  };

  const lbsToKg = (value: any) => {
    const lbs = Number(value);
    if (!value || Number.isNaN(lbs)) return "";
    return formatDecimal(lbs / 2.20462);
  };

  const normalizeHeightInput = (value: string, unit: string) =>
    unit === "cm" ? value.replace(/[^0-9]/g, "") : sanitizeHeightFtInTyping(value);

  const sanitizeWeightInput = (value: string) =>
    value.replace(/[^0-9.]/g, "").replace(/(\..*?)\..*/g, "$1");

  const handleHeightUnitChange = (nextUnit: "cm" | "ft/in") => {
    if (nextUnit === units.height) return;
    setQuestionnaireData((prev: any) => ({
      ...prev,
      height:
        nextUnit === "ft/in"
          ? cmToFeetInches(prev.height)
          : feetInchesToCm(prev.height),
    }));
    setUnits((prev) => ({ ...prev, height: nextUnit }));
  };

  const handleWeightUnitChange = (nextUnit: "kg" | "lbs") => {
    if (nextUnit === units.weight) return;
    setQuestionnaireData((prev: any) => ({
      ...prev,
      weight:
        nextUnit === "lbs"
          ? kgToLbs(prev.weight)
          : lbsToKg(prev.weight),
    }));
    setUnits((prev) => ({ ...prev, weight: nextUnit }));
  };

  React.useEffect(() => {
    if (!open) return;

    const onKeyDownCapture = (ev: KeyboardEvent) => {
      const isSpace =
        ev.key === " " || ev.code === "Space" || (ev as any).key === "Spacebar";
      if (!isSpace) return;

      const menu = document.querySelector(".react-select__menu .react-select__option");
      if (!menu) return;

      // Block space completely when any react-select menu is open
      ev.preventDefault();
      ev.stopImmediatePropagation();
    };

    // Capture phase so we can block before react-select handles it.
    document.addEventListener("keydown", onKeyDownCapture, true);
    return () => document.removeEventListener("keydown", onKeyDownCapture, true);
  }, [open]);

  // `profile` page par `userId` undefined ho sakta hai (sirf `patientId` available hota hai),
  // isliye primaryUserId ka fallback `propPatientId` se kar rahe hain.
  const primaryUserId = userId ?? propPatientId;

  const { data: relatedUsersResponse } = useGetPatientListQuery(primaryUserId, {
    skip: !open || !primaryUserId,
  });

  const relationshipTypeOptions = React.useMemo(() => {
    const items = relatedUsersResponse?.data || relatedUsersResponse?.items || [];
    const rawValues = Array.isArray(items)
      ? items
        .map((it: any) => it?.relationshipType ?? it?.relationship ?? it?.relationship_type)
        .filter(Boolean)
      : [];
    // Normalize for correct dedupe (API may contain whitespace differences)
    const unique = Array.from(
      new Set(rawValues.map((v) => String(v).trim().toUpperCase()).filter(Boolean))
    );

    return unique;
  }, [relatedUsersResponse]);

  const relationshipTypeSelectOptions = React.useMemo(() => {
    const fallback = ["SPOUSE", "PARENT", "CHILD", "SIBLING", "FRIEND", "OTHER"];
    const rels = relationshipTypeOptions.length ? relationshipTypeOptions : fallback;
    return rels.map((rel) => ({
      value: rel,
      label: rel.charAt(0).toUpperCase() + rel.slice(1).toLowerCase(),
    }));
  }, [relationshipTypeOptions]);

  const selectedRelationshipOption = React.useMemo(() => {
    const current = String(questionnaireData.emergencyRelationship ?? "")
      .trim()
      .toUpperCase();
    return relationshipTypeSelectOptions.find((o) => o.value === current) || null;
  }, [questionnaireData.emergencyRelationship, relationshipTypeSelectOptions]);

  useEffect(() => {
    if (open) {
      setConditionSearch("");
      setAllergySearch("");
      setMedicationSearch("");
      setSupplementSearch("");
      setFamilySearch("");
      if (!initialData) {
        setUnits({ height: "cm", weight: "kg" });
      }
    }
  }, [open, initialData]);

  useEffect(() => {
    if (initialData && open) {
      const medicalInfo = initialData.medicalInformation || {};
      const conditions = initialData.diagnosedConditions || medicalInfo.conditions || [];
      const allergies = initialData.allergies || medicalInfo.allergies || [];
      const medications = initialData.medications || medicalInfo.medications || [];
      const familyHistory = initialData.familyHistoryDiseases || medicalInfo.familyHistory || [];
      const supplements = initialData.supplements || medicalInfo.supplements || [];

      // Deduplicate arrays by id to prevent duplicate chips in multi-selects
      const dedup = (arr: any[]) => {
        const seen = new Set<string>();
        return arr.filter((item) => {
          const key = String(item?.id ?? "").trim();
          if (!key || seen.has(key)) return false;
          seen.add(key);
          return true;
        });
      };

      const uniqueConditions = dedup(conditions);
      const uniqueAllergies = dedup(allergies);
      const uniqueMedications = dedup(medications);
      const uniqueFamilyHistory = dedup(familyHistory);
      const uniqueSupplements = dedup(supplements);

      const mappedData = {
        ...questionnaireData,

        ...initialData,
        firstName: initialData.firstName ? initialData.firstName : storedUser?.firstName,
        middleName: initialData.middleName ? initialData.middleName : storedUser?.middleName,
        lastName: initialData.lastName ? initialData.lastName : storedUser?.lastName,
        emergencyRelationship: initialData.emergencyRelationship
          ? String(initialData.emergencyRelationship).toUpperCase()
          : "",
        height: initialData.heightInCm || initialData.height || "",
        weight: initialData.weightInKg || initialData.weight || "",
        gender: initialData.gender || initialData.genderIdentity || null,
        sexAssignedAtBirth: initialData.sexAssignedAtBirth || null,
        emergencyContactConsent: initialData.emergencyContactConsent !== undefined
          ? String(initialData.emergencyContactConsent)
          : null,
        currentlyTakingMedications: initialData.currentlyTakingMedications != undefined
          ? String(initialData.currentlyTakingMedications)
          : "false",
        diagnosedConditionIds: uniqueConditions.map((c: any) => c.id) || [],
        allergyIds: uniqueAllergies.map((a: any) => a.id) || [],
        patientMedicationIds: uniqueMedications.map((m: any) => m.id) || [],
        familyHistoryDiseaseIds: uniqueFamilyHistory.map((f: any) => f.id) || [],
        supplementIds: uniqueSupplements.map((s: any) => s.id) || [],
      };

      setQuestionnaireData(mappedData);
      setUnits({
        height: inferHeightUnit(mappedData.height),
        weight: inferWeightUnit(mappedData.weight),
      });

      if (uniqueConditions.length > 0) {
        setSelectedConditions(uniqueConditions.map((c: any) => ({
          id: c.id,
          name: c.parentName || c.name,
          descriptions: c.shortDescription || c.name
        })));
      }
      if (uniqueAllergies.length > 0) {
        setSelectedAllergies(uniqueAllergies.map((a: any) => ({
          id: a.id,
          name: a.parentName || a.name,
          descriptions: a.shortDescription || a.name
        })));
      }
      if (uniqueMedications.length > 0) {
        setSelectedMedications(uniqueMedications.map((m: any) => ({
          id: m.id,
          name: m.name,
          descriptions: m.name
        })));
      }
      if (uniqueFamilyHistory.length > 0) {
        setSelectedFamilyHistory(uniqueFamilyHistory.map((f: any) => ({
          id: f.id,
          name: f.parentName || f.name,
          descriptions: f.shortDescription || f.name
        })));
      }
      if (uniqueSupplements.length > 0) {
        setSelectedSupplements(uniqueSupplements.map((s: any) => ({
          id: s.id,
          name: s.name,
          descriptions: s.name
        })));
      }
    }
  }, [initialData, open]);





  const { data: conditionResults } = useGetallDiagnosisDeaseseQuery(conditionSearch, {
    skip: !open || conditionSearch.length === 1,
  });
  const { data: allergyResults } = useGetallergiesChronicDeaseseQuery(allergySearch, {
    skip: !open || allergySearch.length === 1,
  });
  const { data: medicationResults } = useGetmedicationSupplementQuery(medicationSearch, {
    skip: !open || medicationSearch.length === 1,
  });
  const { data: supplementResults } = useGetmedicationSupplementQuery(supplementSearch, {
    skip: !open || supplementSearch.length === 1,
  });
  const { data: familyResults } = useGetallHerediateryDeaseseQuery(familySearch, {
    skip: !open || familySearch.length === 1,
  });

  const handleQChange = (field: string, value: any) => {
    setQuestionnaireData((prev: any) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev: any) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleMultiSelectChange = (field: string, setter: (val: any[]) => void, val: any[]) => {
    const selected = val || [];
    setter(selected);
    handleQChange(field, selected.map((o: any) => o.id));
  };

  const mergeOptions = (selected: any[] = [], apiOptions: any[] = []) => {
    const map = new Map<string, any>();

    const keyOf = (item: any) => {
      const id = item?.id ?? item?.value ?? item?.name ?? item?.parentName ?? item?.relationshipType ?? item?.relationship;
      return id === undefined || id === null || id === "" ? null : String(id).trim().toUpperCase();
    };

    [...selected, ...apiOptions].forEach((item) => {
      const key = keyOf(item);
      if (!key) return;

      // Ensure we always have `id` so react-select getOptionValue(o => o.id) works.
      const normalized = { ...item, id: item?.id ?? item?.name ?? item?.parentName ?? item?.relationshipType ?? item?.relationship };
      map.set(key, normalized);
    });

    return Array.from(map.values());
  };

  const conditionOptions = mergeOptions(
    selectedConditions,
    conditionResults?.data?.map((item: any) => ({
      id: item.id,
      name: item.parentName,
      descriptions: item.shortDescription,
    })) || []
  );

  const allergyOptions = mergeOptions(
    selectedAllergies,
    allergyResults?.data?.map((item: any) => ({
      id: item.id,
      name: item.parentName,
      descriptions: item.shortDescription,
    })) || []
  );

  const medicationOptions = mergeOptions(
    selectedMedications,
    medicationResults?.data?.map((item: any) => ({
      id: item.id,
      name: item.name,
      descriptions: item.name,
    })) || []
  );

  const supplementOptions = mergeOptions(
    selectedSupplements,
    supplementResults?.data?.map((item: any) => ({
      id: item.id,
      name: item.name,
      descriptions: item.name,
    })) || []
  );

  const familyOptions = mergeOptions(
    selectedFamilyHistory,
    familyResults?.data?.map((item: any) => ({
      id: item.id,
      name: item.parentName,
      descriptions: item.shortDescription,
    })) || []
  );

  const steps = [
    "Basic Info",
    "Emergency",
    "Health Overview",
    "Medications",
    "Family History",
    "Goals",
    "Final Input",
  ];


  const getAgeInMonths = (dob: any) => {
    if (!dob) return null;

    const birth = new Date(dob);
    const today = new Date();

    let months =
      (today.getFullYear() - birth.getFullYear()) * 12 +
      (today.getMonth() - birth.getMonth());

    return months;
  };
  const convertFtInToCm = (value: any) => {
    if (!value) return null;

    const match = value.match(/(\d+)[^\d]+(\d+)/); // e.g. 5'10
    if (!match) return null;

    const ft = parseInt(match[1] || 0);
    const inch = parseInt(match[2] || 0);

    return ft * 30.48 + inch * 2.54;
  };
  const validateStep = (step: number) => {
    const q = questionnaireData;
    let newErrors: any = {};

    if (step === 0) {
      try { nameValidation.validateSync(q.firstName); } catch (e: any) { newErrors.firstName = e.message; }
      try { nameValidation.validateSync(q.lastName); } catch (e: any) { newErrors.lastName = e.message; }
      if (!q.dateOfBirth) newErrors.dateOfBirth = "Date of Birth is required";
      if (!q.gender) newErrors.gender = "Gender identity is required";
      if (!q.sexAssignedAtBirth) newErrors.sexAssignedAtBirth = "Sex assigned at birth is required";
      const ageInMonths = getAgeInMonths(q.dateOfBirth);
      const heightValue = String(q?.height || "").trim();
      if (!heightValue) {
        newErrors.height = "Height is required";
      } else {
        let height =
          units.height === "cm"
            ? parseFloat(q.height)
            : convertFtInToCm(q.height);


        if (ageInMonths !== null && ageInMonths <= 1) {
          if (!height || height < 45 || height > 60) {
            newErrors.height = "For newborn, height must be between 45–60 cm";
          }
        } else {

          const hErr =
            units.height === "cm"
              ? validateHeightCm(String(q.height))
              : validateHeightFtIn(String(q.height));

          if (hErr) newErrors.height = hErr;
        }
      }

      const weightValue = String(q?.weight || "").trim();
      if (!weightValue) {
        newErrors.weight = "Weight is required";
      } else {
        let weight = parseFloat(q.weight);


        if (units.weight === "lbs") {
          weight = weight * 0.453592;
        }


        if (ageInMonths !== null && ageInMonths <= 1) {
          if (!weight || weight < 2 || weight > 5) {
            newErrors.weight = "For newborn, weight must be between 2–5 kg";
          }
        } else {

          const wErr =
            units.weight === "kg"
              ? validateWeightKg(String(q.weight))
              : validateWeightLbs(String(q.weight));

          if (wErr) newErrors.weight = wErr;
        }
      }


      if (!q.currentLocation?.trim()) newErrors.currentLocation = "Location is required";
      if (!q.bloodGroup?.trim()) newErrors.bloodGroup = "Blood Group is required";
      const dob = q.dateOfBirth;
      const minDate = "1926-01-01";
      const maxDate = new Date().toISOString().split("T")[0];

      if (!dob) {
        newErrors.dateOfBirth = "Date of Birth is required";
      } else if (dob < minDate) {
        newErrors.dateOfBirth = "Date cannot be before 01-01-1926";
      } else if (dob > maxDate) {
        newErrors.dateOfBirth = "Future dates are not allowed";
      }
    } else if (step === 1) {
      try { nameValidation.validateSync(q.emergencyContactName); } catch (e: any) { newErrors.emergencyContactName = e.message; }
      if (!q.emergencyRelationship?.trim()) newErrors.emergencyRelationship = "Relationship is required";
      try { phoneValidation.validateSync(q.emergencyPhoneNumber); } catch (e: any) { newErrors.emergencyPhoneNumber = e.message; }
      if (!q.emergencyContactConsent) newErrors.emergencyContactConsent = "Consent is required";
    } if (step === 3) {
      console.log(q)
      if (q.currentlyTakingMedications == "" || q.currentlyTakingMedications == "null") newErrors.currentlyTakingMedications = "Please indicate if you are currently taking medications *";
      if (q.currentlyTakingMedications == "true" && !q.patientMedicationIds.length) newErrors.patientMedicationIds = "Patient Medication is required*";
      if (q.currentlyTakingMedications == "false") delete newErrors.patientMedicationIds;

    }
    console.log(newErrors)
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
    }
  };

  const handleBack = () => setActiveStep((prev) => Math.max(prev - 1, 0));

  const handleSaveQuestionnaire = async () => {
    if (validateStep(activeStep)) {
      try {
        const patientId = propPatientId || questionnaireData.patientId || initialData?.patientId || userId;

        // Construct clean payload with only relevant fields
        const payload: any = {
          firstName: questionnaireData.firstName,
          middleName: questionnaireData.middleName,
          lastName: questionnaireData.lastName,
          bloodGroup: questionnaireData.bloodGroup,
          dateOfBirth: questionnaireData.dateOfBirth,
          gender: questionnaireData.gender,
          sexAssignedAtBirth: questionnaireData.sexAssignedAtBirth,
          height: questionnaireData.height,
          weight: questionnaireData.weight,
          currentLocation: questionnaireData.currentLocation,
          emergencyContactName: questionnaireData.emergencyContactName,
          emergencyRelationship: questionnaireData.emergencyRelationship,
          emergencyPhoneNumber: questionnaireData.emergencyPhoneNumber,
          emergencyContactConsent: questionnaireData.emergencyContactConsent === "true",
          diagnosedConditionIds: questionnaireData.diagnosedConditionIds,
          allergyIds: questionnaireData.allergyIds,
          currentlyTakingMedications: questionnaireData.currentlyTakingMedications === "true",
          patientMedicationIds: questionnaireData.patientMedicationIds,
          supplementIds: questionnaireData.supplementIds,
          familyHistoryDiseaseIds: questionnaireData.familyHistoryDiseaseIds,
          goals: questionnaireData.goals,
          finalInput: questionnaireData.finalInput,
          userId: userId,
          patientId: patientId,
        };

        let response: any;

        if (mode == 'update') {
          response = await updatePatient({ id: patientId, ...payload }).unwrap();
        } else {
          response = await createPatient(payload).unwrap();
          if (response?.data?.coinSummary?.coinsEarned) {
            triggerCoins(response?.data?.coinSummary?.coinsEarned ? response?.data?.coinSummary?.coinsEarned : 0, response?.data?.coinSummary?.totalCoins ? response?.data?.coinSummary?.totalCoins : 0)

          }
        }


        const toastMsg = (typeof response?.data === "string" && response.data) || response?.message || (mode == 'update' ? "Patient updated successfully." : "Patient created successfully.");
        toast.success(toastMsg);
        onClose();
      } catch (error: any) {
        toast.error(error?.data?.message || "Failed to save questionnaire");
      }
    }
  };

  const selectStyles = {
    control: (base: any) => ({
      ...base,
      minHeight: "38px",
      borderColor: "#ced4da",
      boxShadow: "none",
      "&:hover": { borderColor: "#86b7fe" },
    }),
    menuPortal: (base: any) => ({ ...base, zIndex: 999999999 }),
  };

  const yesNoOptions = [
    { value: "true", label: "Yes" },
    { value: "false", label: "No" },
  ];

  const genderIdentityOptions = [
    { value: "MALE", label: "Male" },
    { value: "FEMALE", label: "Female" },
    { value: "PREFER_NOT_TO_SAY", label: "Prefer not to say" },
  ];

  const sexOptions = [
    { value: "MALE", label: "Male" },
    { value: "FEMALE", label: "Female" },
    { value: "PREFER_NOT_TO_SAY", label: "Prefer not to say" },
  ];

  const stepContents = [
    // Step 0 - Basic Info
    <div className="row g-3" key="step-0">
      <div className="col-md-6">
        <label className="form-label" htmlFor="firstName">First Name <span className="text-danger">*</span></label>
        <input
          type="text"
          id="firstName"
          className={`form-control ${errors.firstName ? "is-invalid" : ""}`}

          value={questionnaireData.firstName || ""}

          onChange={(e) => {
            let value = e.target.value;

            // Allow only alphabets + space
            value = value.replace(/[^a-zA-Z\s]/g, "");

            // Remove leading spaces
            value = value.trimStart();

            setQuestionnaireData({
              ...questionnaireData,
              firstName: value,
            });
          }}
          onBlur={() =>
            setQuestionnaireData((prev: any) => ({
              ...prev,
              firstName: prev.firstName
                ? prev.firstName.charAt(0).toUpperCase() +
                prev.firstName.slice(1).toLowerCase()
                : "",
            }))
          }
        />
        {errors.firstName && <div className="invalid-feedback">{errors.firstName}</div>}
      </div>
      <div className="col-md-6">
        <label className="form-label" htmlFor="middleName">Middle Name</label>
        <input
          type="text"
          id="middleName"
          className="form-control"
          placeholder="Middle Name"
          value={questionnaireData.middleName || ""}


          onChange={(e) => {
            let value = e.target.value;

            // Allow only alphabets + space
            value = value.replace(/[^a-zA-Z\s]/g, "");

            // Remove leading spaces
            value = value.trimStart();

            setQuestionnaireData({
              ...questionnaireData,
              middleName: value,
            });
          }}
          onBlur={() =>
            setQuestionnaireData((prev: any) => ({
              ...prev,
              middleName: prev.middleName
                ? prev.middleName.charAt(0).toUpperCase() +
                prev.middleName.slice(1).toLowerCase()
                : "",
            }))
          }
        />
      </div>
      <div className="col-md-6">
        <label className="form-label" htmlFor="lastName">Last Name <span className="text-danger">*</span></label>
        <input
          type="text"
          id="lastName"
          className={`form-control ${errors.lastName ? "is-invalid" : ""}`}
          value={questionnaireData.lastName || ""}


          onChange={(e) => {
            let value = e.target.value;

            // Allow only alphabets + space
            value = value.replace(/[^a-zA-Z\s]/g, "");

            // Remove leading spaces
            value = value.trimStart();
            setQuestionnaireData({
              ...questionnaireData,
              lastName: value,
            })
          }}
          onBlur={() =>
            setQuestionnaireData((prev: any) => ({
              ...prev,
              lastName: prev.lastName
                ? prev.lastName.charAt(0).toUpperCase() +
                prev.lastName.slice(1).toLowerCase()
                : "",
            }))
          }
        />
        {errors.lastName && <div className="invalid-feedback">{errors.lastName}</div>}
      </div>
      <div className="col-md-6">
        <label className="form-label" htmlFor="dateOfBirth">Date of Birth <span className="text-danger">*</span></label>
        <input
          min="1926-01-01"
          max={
            new Date(
              new Date().setFullYear(new Date().getFullYear() - 15)
            )
              .toISOString()
              .split("T")[0]
          }
          type="date"
          id="dateOfBirth"
          className={`form-control ${errors.dateOfBirth ? "is-invalid" : ""}`}
          value={questionnaireData.dateOfBirth}
          onChange={(e) => {
            const val = e.target.value; // This will be "2027-01-01"

            // Update the state immediately so the user can see what they are typing
            handleQChange("dateOfBirth", val);

            // Then run your validation logic to show the error message
            const minDate = "1926-01-01";
            const maxDate = new Date().toISOString().split("T")[0];
            let errorMsg = "";

            if (!val) {
              errorMsg = "Date of Birth is required";
            } else if (val < minDate) {
              errorMsg = "Date cannot be before 01-01-1926";
            } else if (val > maxDate) {
              errorMsg = "Future dates are not allowed";
            }

            setErrors((prev: any) => ({ ...prev, dateOfBirth: errorMsg }));
          }}
        />
        {errors.dateOfBirth && <div className="invalid-feedback">{errors.dateOfBirth}</div>}
      </div>
      <div className="col-md-6">
        <label className="form-label" htmlFor="gender">Gender Identity <span className="text-danger">*</span></label>
        <Select
          classNamePrefix="react-select"
          id="gender"
          placeholder="Select"
          options={genderIdentityOptions}
          styles={selectStyles}
          value={genderIdentityOptions.find((o) => o.value === questionnaireData.gender)}
          onChange={(val: any) => handleQChange("gender", val?.value)}
          menuPortalTarget={document.body}
        />
        {errors.gender && (
          <div className="text-danger small mt-1">{errors.gender}</div>
        )}
      </div>
      <div className="col-md-6">
        <label className="form-label" htmlFor="sexAssignedAtBirth">Sex assigned at birth <span className="text-danger">*</span></label>
        <Select
          classNamePrefix="react-select"
          id="sexAssignedAtBirth"
          placeholder="Select"
          options={sexOptions}
          styles={selectStyles}
          value={sexOptions.find((o) => o.value === questionnaireData.sexAssignedAtBirth)}
          onChange={(val: any) => handleQChange("sexAssignedAtBirth", val?.value)}
          menuPortalTarget={document.body}
        />
        {errors.sexAssignedAtBirth && (
          <div className="text-danger small mt-1">{errors.sexAssignedAtBirth}</div>
        )}
      </div>
      <div className="col-md-6">
        <div className="d-flex justify-content-between align-items-end mb-2">
          <label className="form-label mb-0" htmlFor="height">Height <span className="text-danger">*</span></label>
          <div className="unit-toggle-container">
            <button
              type="button"
              className={`unit-btn ${units.height === 'cm' ? 'active' : ''}`}
              onClick={() => handleHeightUnitChange("cm")}
            >CM</button>
            <button
              type="button"
              className={`unit-btn ${units.height === 'ft/in' ? 'active' : ''}`}
              onClick={() => handleHeightUnitChange("ft/in")}
            >FT/IN</button>
          </div>
        </div>
        <div className="input-group">
          <input
            type="text"
            id="height"
            disabled={!questionnaireData.dateOfBirth}
            className={`form-control ${errors.height ? "is-invalid" : ""}`}
            placeholder={units.height === "cm" ? "175" : "Type e.g. 60 → 6'0\""}
            value={questionnaireData.height}
            onChange={(e) => {
              const rawValue = e.target.value;

              let value =
                units.height === "cm"
                  ? rawValue.replace(/[^0-9]/g, "")
                  : sanitizeHeightFtInTyping(rawValue);


              if (units.height === "cm") {
                if (value && parseInt(value) > 300) {
                  value = "300"; // clamp to max
                }
              }

              handleQChange("height", value);


              const ageInMonths = getAgeInMonths(questionnaireData.dateOfBirth);

              let height = null;

              if (units.height === "cm") {
                height = parseFloat(value);
              } else {
                height = convertFtInToCm(value);
              }

              let errorMsg = "";

              if (ageInMonths !== null && ageInMonths <= 1) {
                if (!value) {
                  errorMsg = "Height is required";
                } else if (!height || height < 45 || height > 60) {
                  errorMsg = "For newborn, height must be between 45–60 cm";
                }
              }

              setErrors((prev: any) => ({
                ...prev,
                height: errorMsg,
              }));
            }}
            onBlur={(e) => {
              if (units.height !== "ft/in") return;
              const v = e.target.value.trim();
              if (!v) return;
              const next = formatHeightFtInInput(v);
              if (next && next !== e.target.value) {
                handleQChange("height", next);
              }
            }}
          />
          <span
            className="input-group-text fs-12 text-muted"
            style={{ width: "52px", justifyContent: "center", backgroundColor: "#f9fafb" }}
          >
            {units.height === "cm" ? "cm" : "ft/in"}
          </span>
        </div>
        {errors.height && (
          <div className="text-danger small mt-1">{errors.height}</div>
        )}
      </div>
      <div className="col-md-6">
        <div className="d-flex justify-content-between align-items-end mb-2">
          <label className="form-label mb-0" htmlFor="weight">Weight <span className="text-danger">*</span></label>
          <div className="unit-toggle-container">
            <button
              type="button"
              className={`unit-btn ${units.weight === 'kg' ? 'active' : ''}`}
              onClick={() => handleWeightUnitChange("kg")}
            >KG</button>
            <button
              type="button"
              className={`unit-btn ${units.weight === 'lbs' ? 'active' : ''}`}
              onClick={() => handleWeightUnitChange("lbs")}
            >LBS</button>
          </div>
        </div>
        <div className="input-group">
          <input
            type="text"
            id="weight"
            className={`form-control ${errors.weight ? "is-invalid" : ""}`}
            placeholder={units.weight === "kg" ? "75" : "165"}
            value={questionnaireData.weight}
            // onChange={(e) => handleQChange("weight", sanitizeWeightInput(e.target.value))}
            disabled={!questionnaireData.dateOfBirth}

            onChange={(e) => {
              let value = e.target.value
                .replace(/[^0-9.]/g, "")
                .replace(/(\..*?)\..*/g, "$1");


              if (units.weight === "kg") {
                if (value && parseFloat(value) > 500) {
                  value = "500"; // clamp to max
                }
              }

              handleQChange("weight", value);

              const ageInMonths = getAgeInMonths(questionnaireData.dateOfBirth);

              let weight = parseFloat(value);


              if (units.weight === "lbs") {
                weight = weight * 0.453592;
              }

              let errorMsg = "";

              if (ageInMonths !== null && ageInMonths <= 1) {
                if (!value) {
                  errorMsg = "Weight is required";
                } else if (weight < 2 || weight > 5) {
                  errorMsg = "For newborn, weight must be between 2–5 kg";
                }
              }

              setErrors((prev: any) => ({
                ...prev,
                weight: errorMsg,
              }));
            }}
          />
          <span
            className="input-group-text fs-12 text-muted"
            style={{ width: "52px", justifyContent: "center", backgroundColor: "#f9fafb" }}
          >
            {units.weight}
          </span>
        </div>
        {errors.weight && (
          <div className="text-danger small mt-1">{errors.weight}</div>
        )}
      </div>
      <div className="col-md-6">
        <label className="form-label" htmlFor="currentLocation">
          Current Location <span className="text-danger">*</span>
        </label>
        <input
          type="text"
          id="currentLocation"
          className={`form-control ${errors.currentLocation ? "is-invalid" : ""}`}
          placeholder="Country, State/Region"
          value={questionnaireData.currentLocation}
          onChange={(e) => handleQChange('currentLocation', e.target.value.trimStart())}
        />
        {errors.currentLocation && (
          <div className="invalid-feedback d-block">{errors.currentLocation}</div>
        )}
      </div>
      <div className="col-md-6">
        <label className="form-label" htmlFor="bloodGroup">
          Blood Group <span className="text-danger">*</span>
        </label>
        <select
          id="bloodGroup"
          className={`form-select ${errors.bloodGroup ? "is-invalid" : ""}`}
          value={questionnaireData.bloodGroup}
          onChange={(e) => handleQChange("bloodGroup", e.target.value)}
        >
          <option value="" disabled hidden>
            Select Blood Group
          </option>
          <option value="A+">A+</option>
          <option value="A-">A-</option>
          <option value="B+">B+</option>
          <option value="B-">B-</option>
          <option value="O+">O+</option>
          <option value="O-">O-</option>
          <option value="AB+">AB+</option>
          <option value="AB-">AB-</option>
        </select>
        {errors.bloodGroup && (
          <div className="invalid-feedback d-block">{errors.bloodGroup}</div>
        )}
      </div>
    </div>,

    // Step 1 - Emergency
    <div className="row g-3" key="step-1">
      <div className="col-md-6">
        <label className="form-label" htmlFor="emergencyContactName">Emergency Contact Name <span className="text-danger">*</span></label>
        <input
          type="text"
          id="emergencyContactName"
          className={`form-control ${errors.emergencyContactName ? "is-invalid" : ""}`}
          value={questionnaireData.emergencyContactName}
          onChange={(e) =>
            handleQChange(
              "emergencyContactName",
              e.target.value.replace(/[^A-Za-z]/g, "")
            )
          }
        />
        {errors.emergencyContactName && <div className="invalid-feedback">{errors.emergencyContactName}</div>}
      </div>
      <div className="col-md-6">
        <label className="form-label" htmlFor="emergencyRelationship">Relationship <span className="text-danger">*</span></label>
        <Select
          classNamePrefix="react-select"
          inputId="emergencyRelationship"
          options={relationshipTypeSelectOptions}
          value={selectedRelationshipOption}
          onChange={(val: any) => handleQChange("emergencyRelationship", val?.value || "")}
          placeholder="Select"
          styles={selectStyles}
          isClearable={false}
          menuPortalTarget={document.body}
        />
        {errors.emergencyRelationship && <div className="text-danger small mt-1">{errors.emergencyRelationship}</div>}
      </div>
      <div className="col-md-6">
        <label className="form-label" htmlFor="emergencyPhoneNumber">Phone Number <span className="text-danger">*</span></label>
        <input
          type="text"
          id="emergencyPhoneNumber"
          className={`form-control ${errors.emergencyPhoneNumber ? "is-invalid" : ""}`}
          value={questionnaireData.emergencyPhoneNumber}
          maxLength={10}
          onChange={(e) => {
            const value = e.target.value.replace(/\D/g, "").slice(0, 10);
            handleQChange("emergencyPhoneNumber", value);
          }}
        />
        {errors.emergencyPhoneNumber && <div className="invalid-feedback">{errors.emergencyPhoneNumber}</div>}
      </div>
      <div className="col-md-6">
        <label className="form-label" htmlFor="emergencyContactConsent">Consent to Contact? <span className="text-danger">*</span></label>
        <Select
          classNamePrefix="react-select"
          id="emergencyContactConsent"
          options={yesNoOptions}
          styles={selectStyles}
          value={yesNoOptions.find((o) => o.value === questionnaireData.emergencyContactConsent)}
          onChange={(val: any) => handleQChange("emergencyContactConsent", val?.value)}
          menuPortalTarget={document.body}
        />
        {errors.emergencyContactConsent && <div className="text-danger mt-1 fs-12">{errors.emergencyContactConsent}</div>}
      </div>
      {(questionnaireData.emergencyContactConsent === "true" || questionnaireData.emergencyContactConsent === "false") && (
        <div className="col-12 fade-in">
          <div
            className="p-3 rounded d-flex align-items-center"
            style={{
              backgroundColor:
                questionnaireData.emergencyContactConsent === "true"
                  ? "#eafaf1"
                  : "#fff3f3",
              borderLeft:
                questionnaireData.emergencyContactConsent === "true"
                  ? "4px solid #28a745"
                  : "4px solid #dc3545",
            }}
          >
            <p
              className="mb-0 small fw-medium d-flex align-items-center"
              style={{ color: "#32325d" }}
            >
              <CheckCircle
                className="me-2"
                style={{
                  fontSize: 18,
                  color:
                    questionnaireData.emergencyContactConsent === "true"
                      ? "#28a745"
                      : "#dc3545",
                }}
              />

              {questionnaireData.emergencyContactConsent === "true" &&
                "Yes, you may contact this person if there is a serious health issue."}

              {questionnaireData.emergencyContactConsent === "false" &&
                "No, please do not contact this person for any health-related emergency."}
            </p>
          </div>
        </div>
      )}
    </div>,

    // Step 2 - Health Overview
    <div className="row g-3" key="step-2">
      <div className="col-12">
        <label className="form-label" htmlFor="diagnosedConditionIds">Diagnosed Conditions</label>
        <Select
          classNamePrefix="react-select"
          id="diagnosedConditionIds"
          isMulti
          options={conditionOptions}
          styles={selectStyles}
          value={selectedConditions}
          onInputChange={(val) => setConditionSearch(val)}
          onChange={(val: any) => handleMultiSelectChange("diagnosedConditionIds", setSelectedConditions, val)}
          menuPortalTarget={document.body}
          getOptionLabel={(o: any) => o.name}
          getOptionValue={(o: any) => o.id}
          formatOptionLabel={(option: any) => (
            <span title={option.descriptions}>{option.descriptions}</span>
          )}
        />
      </div>
      <div className="col-12">
        <label className="form-label" htmlFor="allergyIds">Allergies</label>
        <Select
          classNamePrefix="react-select"
          id="allergyIds"
          isMulti
          options={allergyOptions}
          styles={selectStyles}
          value={selectedAllergies}
          onInputChange={(val) => setAllergySearch(val)}
          onChange={(val: any) => handleMultiSelectChange("allergyIds", setSelectedAllergies, val)}
          menuPortalTarget={document.body}
          getOptionLabel={(o: any) => o.name}
          getOptionValue={(o: any) => o.id}
          formatOptionLabel={(option: any) => (
            <span title={option.descriptions}>{option.descriptions}</span>
          )}
        />
      </div>
    </div>,

    // Step 3 - Medications
    <div className="row g-3" key="step-3">
      <div className="col-md-6">
        <label className="form-label" htmlFor="currentlyTakingMedications">Taking Medications? <span className="text-danger">*</span></label>
        <Select
          classNamePrefix="react-select"
          id="currentlyTakingMedications"
          options={yesNoOptions}
          styles={selectStyles}
          value={yesNoOptions.find((o) => o.value === questionnaireData.currentlyTakingMedications)}
          onChange={(val: any) => {




            handleQChange("currentlyTakingMedications", val?.value)

          }}
          menuPortalTarget={document.body}
        />
        {errors.currentlyTakingMedications && <div className="text-danger small mt-1">{errors.currentlyTakingMedications}</div>}
      </div>
      <div className="col-md-6">
        <label className="form-label" htmlFor="patientMedicationIds">Medications <span className="text-danger">*</span></label>
        <Select
          classNamePrefix="react-select"
          id="patientMedicationIds"
          isMulti
          options={medicationOptions}
          styles={selectStyles}
          value={selectedMedications}
          onInputChange={(val) => setMedicationSearch(val)}
          onChange={(val: any) => handleMultiSelectChange("patientMedicationIds", setSelectedMedications, val)}
          menuPortalTarget={document.body}
          getOptionLabel={(o: any) => o.name}
          getOptionValue={(o: any) => o.id}
          placeholder="Medication name(s)"
          formatOptionLabel={(option: any) => (
            <span title={option.descriptions}>{option.descriptions}</span>
          )}
        />
        {errors.patientMedicationIds && <div className="text-danger small mt-1">{errors.patientMedicationIds}</div>}

      </div>
      <div className="col-12">
        <label className="form-label" htmlFor="supplementIds">Supplements / Non-prescription Remedies</label>
        <Select
          classNamePrefix="react-select"
          id="supplementIds"
          isMulti
          options={supplementOptions}
          styles={selectStyles}
          value={selectedSupplements}
          onInputChange={(val) => setSupplementSearch(val)}
          onChange={(val: any) => handleMultiSelectChange("supplementIds", setSelectedSupplements, val)}
          menuPortalTarget={document.body}
          getOptionLabel={(o: any) => o.name}
          getOptionValue={(o: any) => o.id}
          placeholder="Select supplements"
          formatOptionLabel={(option: any) => (
            <span title={option.descriptions}>{option.descriptions}</span>
          )}
        />
      </div>
    </div>,

    // Step 4 - Family History
    <div className="row g-3" key="step-4">
      <div className="col-12">
        <label className="form-label" htmlFor="familyHistoryDiseaseIds">Family History</label>
        <Select
          classNamePrefix="react-select"
          id="familyHistoryDiseaseIds"
          isMulti
          options={familyOptions}
          styles={selectStyles}
          value={selectedFamilyHistory}
          onInputChange={(val) => setFamilySearch(val)}
          onChange={(val: any) => handleMultiSelectChange("familyHistoryDiseaseIds", setSelectedFamilyHistory, val)}
          menuPortalTarget={document.body}
          getOptionLabel={(o: any) => o.name}
          getOptionValue={(o: any) => o.id}
        />
      </div>
    </div>,

    // Step 5 - Goals
    <div className="row g-3" key="step-5">
      <div className="col-12">
        <label className="form-label" htmlFor="goals">Health Goals</label>
        <textarea
          id="goals"
          placeholder="e.g., Manage blood pressure, lose weight, improve fitness, control diabetes, reduce stress..."
          className="form-control"
          rows={4}
          maxLength={2000}
          value={questionnaireData.goals}
          onChange={(e) => handleQChange("goals", e.target.value.trimStart())}
        />
        <div
          style={{
            textAlign: "right",
            fontSize: "12px",
            color: questionnaireData.goals?.length > 2000 ? "red" : "#888"
          }}
        >
          {(questionnaireData.goals?.length || 0)}/{2000}
        </div>
      </div>
    </div>,

    // Step 6 - Final Input
    <div className="row g-3" key="step-6">
      <div className="col-12">
        <label className="form-label" htmlFor="finalInput">Anything else?</label>
        <textarea
          placeholder="Any additional information you'd like your doctor to know? (e.g., allergies, past surgeries, ongoing symptoms, concerns...)"
          id="finalInput"
          className="form-control"
          rows={4}
          maxLength={2000}
          value={questionnaireData.finalInput}
          onChange={(e) => handleQChange("finalInput", e.target.value.trimStart())}
        />
        <div
          style={{
            textAlign: "right",
            fontSize: "12px",
            color: questionnaireData.finalInput?.length > 2000 ? "red" : "#888"
          }}
        >
          {(questionnaireData.finalInput?.length || 0)}/{2000}
        </div>
      </div>
    </div>,
  ];

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      className="auth-modal glass-modal"
      // onClose={onClose}
      onClose={(event, reason) => {
        // Prevent closing on backdrop click & ESC
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
        onClose()
      }}
      BackdropProps={{
        style: { backgroundColor: "rgba(0,0,0,0.2)", backdropFilter: "blur(5px)" },
      }}
    >
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pt: 3, pb: 5 }}>
        <h3 style={{ margin: 0 }}>Initial Health Questionnaire</h3>
        <IconButton onClick={onClose} sx={{ color: "red" }} >
          <CloseIcon sx={{ color: "gray", "&:hover": { color: "darkred" } }} />
        </IconButton>
      </DialogTitle>

      <DialogContent className="px-4 pb-3" onKeyDown={handleModalKeyDown}>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <form>{stepContents[activeStep]}</form>
      </DialogContent>

      <DialogActions className="pb-3 px-4 d-flex justify-content-end gap-2">
        <button className="btn btn-warning" disabled={activeStep === 0} onClick={handleBack}>
          Back
        </button>
        {activeStep === steps.length - 1 ? (
          <button className="btn btn-primary" onClick={handleSaveQuestionnaire} disabled={isLoading}>
            {isLoading ? (mode === "update" ? "Updating..." : "Saving...") : (mode === "update" ? "Update" : "Save")}
          </button>
        ) : (
          <button className="btn btn-primary" onClick={handleNext}>
            Next
          </button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default HealthQuestionnaireModal;
